import { privateApi } from '@services/api';
import {
  AppliedJobsResponse,
  CompaniesSuggestion,
  JobPreferencePayload,
  JobQuestion,
  JobQuestionResponse,
  JobRecommendationResponse,
  JobRecommendationsPreferencesPayload,
  JobRecommendationsPreferencesResponse,
  SavedJobResponse,
  SavedJobsResponse,
  WorkerAvailabilityResponse,
  WorkerJobCommonResultDTO,
} from '@worker/types/myJobs.type';
import { buildURLSearchParams } from '@worker/helpers/my-jobs-helpers';

export const MAIN_URL = 'worker';

export const getSuggestedCompanies = async (
  query: string
): Promise<CompaniesSuggestion> => {
  const result = await privateApi.get<CompaniesSuggestion>(
    'companies/suggest',
    {
      params: { query },
    }
  );
  return result.data;
};

// TODO: Consider to remove it, we should use the saveJobRecommendationsPreferences instead
export const saveJobPreferences = async (
  preferences: JobPreferencePayload
): Promise<number[]> => {
  const result = await privateApi.post<number[]>(
    `${MAIN_URL}/job-preferences`,
    preferences
  );
  return result.data;
};

export const getJobRecommendations =
  async (): Promise<JobRecommendationResponse> => {
    const result = await privateApi.get<JobRecommendationResponse>(
      `${MAIN_URL}/job-recommendations`
    );
    return result.data;
  };

/**
 * Parameters for job recommendations search
 * Note: companyIds and titleIds support arrays for Grails multi-value query params
 * buildURLSearchParams will convert arrays to ?key=val1&key=val2 format
 */
export interface JobRecommendationSearchParams {
  // Pagination
  max?: number;
  offset?: number;

  // Basic filters - companyIds supports array for multi-value params
  companyIds?: string | string[];
  address?: string;
  addressLatitude?: number;
  addressLongitude?: number;
  maxMilesAway?: number;

  // Sort
  sort?: string;
  order?: 'asc' | 'desc';

  // Advanced filters - titleIds supports array for multi-value params
  titleIds?: string | string[];
  startDate?: string;
  endDate?: string;
  onGoing?: boolean;
  minimumHourlyRate?: number;
  minimumDailyRate?: number;
  location?: string;
  includeNationalJob?: boolean;
}

/**
 * Fetch NEW job recommendations for the worker
 */
export const listNewJobRecommendations = async (
  params?: JobRecommendationSearchParams
): Promise<JobRecommendationResponse> => {
  const searchParams = buildURLSearchParams(params);

  const result = await privateApi.get<JobRecommendationResponse>(
    `${MAIN_URL}/job-recommendations/new`,
    {
      params: searchParams,
      paramsSerializer: () => searchParams.toString(),
    }
  );
  return result.data;
};

export const getJobRecommendationById = async (
  jobRecommendationId: number
): Promise<WorkerJobCommonResultDTO> => {
  const result = await privateApi.get<WorkerJobCommonResultDTO>(
    `${MAIN_URL}/job-recommendations/${jobRecommendationId}`
  );
  return result.data;
};

/**
 * Fetch APPLIED job recommendations for the worker
 */
export const listAppliedJobRecommendations = async (
  params?: JobRecommendationSearchParams
): Promise<AppliedJobsResponse> => {
  const searchParams = buildURLSearchParams(params);

  const result = await privateApi.get<AppliedJobsResponse>(
    `${MAIN_URL}/jobs/applied`,
    {
      params: searchParams,
      paramsSerializer: () => searchParams.toString(),
    }
  );
  return result.data;
};

/**
 * Fetch job questions for a specific job
 * @param jobId - The ID of the job to fetch questions for
 * @returns Array of job questions (empty if no questions)
 */
export const fetchJobQuestions = async (
  jobId: number
): Promise<JobQuestion[]> => {
  const result = await privateApi.get<JobQuestion[]>(
    `${MAIN_URL}/jobs/${jobId}/questions`
  );
  return result.data;
};

/**
 * Apply to a job
 * @param jobId - The ID of the job to apply to
 * @param note - Optional application note
 * @param responses - Optional question responses
 * @param files - Optional map of questionId to File for attach-file/video questions
 * @returns The updated job data with jobApplication
 */
export const applyToJob = async (
  jobId: number,
  note?: string,
  responses?: JobQuestionResponse[],
  files?: Map<number, File>
): Promise<WorkerJobCommonResultDTO> => {
  // If no files, use simple JSON (more efficient for text-only responses)
  if (!files || files.size === 0) {
    const result = await privateApi.post<WorkerJobCommonResultDTO>(
      `${MAIN_URL}/jobs/${jobId}/apply`,
      { note, responses }
    );
    return result.data;
  }

  // With files: use FormData (multipart/form-data)
  const formData = new FormData();
  if (note) {
    formData.append('note', note);
  }

  // Add text responses and file references
  responses?.forEach((response, index) => {
    formData.append(
      `responses[${index}].questionId`,
      String(response.questionId)
    );
    formData.append(`responses[${index}].data`, response.data);
  });

  // Append actual files using the reference key
  files.forEach((file, questionId) => {
    const fileKey = `file_${questionId}`;
    formData.append(fileKey, file);
  });

  const result = await privateApi.post<WorkerJobCommonResultDTO>(
    `${MAIN_URL}/jobs/${jobId}/apply`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return result.data;
};

/**
 * Un-apply from a job
 * @param jobId - The ID of the job to un-apply from
 */
export const unApplyFromJob = async (jobId: number): Promise<void> => {
  await privateApi.post(`${MAIN_URL}/jobs/${jobId}/un-apply`);
};

export const dismissJobRecommendations = async (
  jobRecommendationIds: number[]
): Promise<void> => {
  const url = `${MAIN_URL}/job-recommendations/dismiss`;
  await privateApi.post(url, {
    jobRecommendationIds,
  });
};

/**
 * Save a job recommendation for later
 * @param jobId - The ID of the job to save
 * @returns The saved job with savedAt timestamp
 */
export const saveJobRecommendation = async (
  jobId: number
): Promise<SavedJobResponse> => {
  const url = `${MAIN_URL}/job-recommendations/${jobId}/save`;
  const result = await privateApi.post<SavedJobResponse>(url);
  return result.data;
};

/**
 * Unsave a previously saved job recommendation
 * @param jobId - The ID of the job to unsave
 */
export const unsaveJobRecommendation = async (jobId: number): Promise<void> => {
  const url = `${MAIN_URL}/job-recommendations/${jobId}/unsave`;
  await privateApi.post(url);
};

/**
 * Fetch SAVED jobs for the worker
 * Uses same query parameter pattern as listNewJobRecommendations
 */
export const listSavedJobs = async (
  params?: JobRecommendationSearchParams
): Promise<SavedJobsResponse> => {
  const searchParams = buildURLSearchParams(params);

  const result = await privateApi.get<SavedJobsResponse>(
    `${MAIN_URL}/jobs/saved`,
    {
      params: searchParams,
      paramsSerializer: () => searchParams.toString(),
    }
  );
  return result.data;
};

export const getJobRecommendationsPreferences =
  async (): Promise<JobRecommendationsPreferencesResponse> => {
    // Note: GET endpoint not implemented yet in backend
    const result = await privateApi.get<JobRecommendationsPreferencesResponse>(
      `${MAIN_URL}/job-recommendations/preferences`
    );
    return result.data;
  };

export const saveJobRecommendationsPreferences = async (
  preferences: JobRecommendationsPreferencesPayload
): Promise<JobRecommendationsPreferencesResponse> => {
  // Using the actual webapp endpoint: /api/webapp/worker/job-preferences
  const result = await privateApi.post<JobRecommendationsPreferencesResponse>(
    `${MAIN_URL}/job-preferences`,
    preferences
  );
  return result.data;
};

export const saveUnavailableDates = async (availabilityData: {
  isAllDay: boolean;
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
}): Promise<{ success: boolean; message: string }> => {
  const result = await privateApi.post<{ success: boolean; message: string }>(
    `${MAIN_URL}/availabilities`,
    availabilityData
  );
  return result.data;
};

export const getWorkerAvailability = async () => {
  try {
    const result = await privateApi.get<{
      workerAvailabilities: WorkerAvailabilityResponse[];
    }>(`${MAIN_URL}/availabilities`);
    return result.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const deleteWorkerAvailability = async (availabilityId: number) => {
  try {
    const result = await privateApi.delete(
      `${MAIN_URL}/availabilities/${availabilityId}`
    );
    return result.data;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const createInNetworkCheckoutSession = async (): Promise<{
  sessionUrl: string;
}> => {
  const result = await privateApi.post<{ url: string }>(
    'checkoutV2/createWorkerInNetworkSession'
  );
  return { sessionUrl: result.data.url };
};

export const processCheckoutSession = async (
  type: string,
  sessionId: string
): Promise<{ message: string; url: string }> => {
  const result = await privateApi.post<{ message: string; url: string }>(
    'checkoutV2/processSession',
    null,
    { params: { type, sessionId } }
  );
  return result.data;
};
